import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { endpoint } from "../../resource/Constant";
import { useNavigate } from "react-router-dom";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';

const STORAGE = {
  HEADER_COLORS: "crawling-theme-header-colors",
  BUBBLE_COLORS: "crawling-theme-bubble-colors",
  HEADER_ANGLE: "crawling-theme-header-angle",
  BUBBLE_ANGLE: "crawling-theme-bubble-angle",
  HEADER_GRADIENT: "crawling-gradient-header",
  BUBBLE_GRADIENT: "crawling-gradient-bubble",
};

const THEME_EVENT = "crawling-theme-updated";

const buildGradient = (angle, colors) =>
  `linear-gradient(${angle}deg, ${colors.map((c) => c.color).join(", ")})`;

const parseColors = (raw, fallback) => {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((c) => c?.id && c?.color)) {
      return parsed;
    }
    return fallback;
  } catch {
    return fallback;
  }
};

const ColorList = ({
  label,
  colors,
  angle,
  onAngleChange,
  onColorChange,
  onAddColor,
  onRemoveColor,
}) => (
  <Card
    variant="outlined"
    sx={{
      mb: 2,
      borderRadius: 2,
      boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
      borderColor: "divider",
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Tune angle and stops for this area
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: 240 }}>
          <Typography variant="body2" sx={{ minWidth: 40 }}>
            Angle
          </Typography>
          <Slider
            size="large"
            value={angle}
            min={0}
            max={360}
            onChange={(_, val) => onAngleChange(val)}
          />
        </Stack>
      </Stack>

      <Stack spacing={1.25} mt={2}>
        {colors.map((item, idx) => (
          <Stack
            key={item.id}
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 60 }}>
                Color {idx + 1}
              </Typography>
              <input
                type="color"
                value={item.color}
                onChange={(e) => onColorChange(item.id, e.target.value)}
                style={{
                  width: 42,
                  height: 32,
                  border: "1px solid #e0e0e0",
                  borderRadius: 8,
                  padding: 2,
                  background: "#fff",
                }}
              />
              <TextField
                size="small"
                value={item.color}
                onChange={(e) => onColorChange(item.id, e.target.value)}
                inputProps={{ style: { fontFamily: "monospace" } }}
              />
            </Stack>
            <IconButton size="small" onClick={() => onRemoveColor(item.id)} disabled={colors.length <= 1}>
              <Trash2 size={16} />
            </IconButton>
          </Stack>
        ))}
        <Button
          startIcon={<Plus size={16} />}
          variant="outlined"
          size="small"
          onClick={onAddColor}
          sx={{ alignSelf: "flex-start", mt: 1 }}
        >
          Add color
        </Button>
      </Stack>
    </CardContent>
  </Card>
);

const ChatbotDynamicTheme = () => {
  // Separate gradients for different chatbot parts
  const defaultHeader = [
    { id: "h1", color: "#1976d2" },
    { id: "h2", color: "#42a5f5" },
    { id: "h3", color: "#66bb6a" },
  ];
  const defaultBubble = [
    { id: "b1", color: "#8e24aa" },
    { id: "b2", color: "#ba68c8" },
    { id: "b3", color: "#f48fb1" },
  ];

  const navigate = useNavigate();

  useEffect(() => {

  axios.get(`${endpoint}/api/crawling/get-color-private` , {headers : {Authorization : `Bearer ${localStorage.getItem("crawling")}`}})
      .then((response) => {
        const header_gradient = response?.data?.data?.header_gradient;
        const user_gradient = response?.data?.data?.user_gradient;

        if (header_gradient?.color?.length > 0) {
          const headerColorsArray = header_gradient.color.map((color , index) => ({
            id: `h${index + 1}`,
            color: color
          }));
          setHeaderColors(headerColorsArray);
          if (header_gradient.angle !== undefined) setHeaderAngle(header_gradient.angle);
        }
        if (user_gradient?.color?.length > 0) {
          const userColorsArray = user_gradient.color.map((color , index) => ({
            id: `b${index + 1}`,
            color: color
          }));
          setBubbleColors(userColorsArray);
          if (user_gradient.angle !== undefined) setBubbleAngle(user_gradient.angle);

        }
      })
      .catch((error) => {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("crawling");
          navigate('/crawling/signin');
          return;
        }
  })
  }, [navigate]);

  const [headerColors, setHeaderColors] = useState(() => parseColors(localStorage.getItem(STORAGE.HEADER_COLORS), defaultHeader));
  const [bubbleColors, setBubbleColors] = useState(() => parseColors(localStorage.getItem(STORAGE.BUBBLE_COLORS), defaultBubble));
  const [headerAngle, setHeaderAngle] = useState(() => Number(localStorage.getItem(STORAGE.HEADER_ANGLE)) || 135);
  const [bubbleAngle, setBubbleAngle] = useState(() => Number(localStorage.getItem(STORAGE.BUBBLE_ANGLE)) || 135);

  

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");

  const showSnackbar = (msg) => {
    setMessage(msg);
    setOpen(true);
  };

  // Hydrate from localStorage on mount
  // useEffect(() => {
  //   const storedHeader = localStorage.getItem(STORAGE.HEADER_COLORS);
  //   const storedBubble = localStorage.getItem(STORAGE.BUBBLE_COLORS);
  //   const storedHeaderAngle = localStorage.getItem(STORAGE.HEADER_ANGLE);
  //   const storedBubbleAngle = localStorage.getItem(STORAGE.BUBBLE_ANGLE);
    
  //   setHeaderColors(parseColors(storedHeader, defaultHeader));
  //   setBubbleColors(parseColors(storedBubble, defaultBubble));
  //   if (storedHeaderAngle) setHeaderAngle(Number(storedHeaderAngle) || 0);
  //   if (storedBubbleAngle) setBubbleAngle(Number(storedBubbleAngle) || 0);
  // }, []);

  const headerGradient = useMemo(() => buildGradient(headerAngle, headerColors), [headerAngle, headerColors]);
  const bubbleGradient = useMemo(() => buildGradient(bubbleAngle, bubbleColors), [bubbleAngle, bubbleColors]);

  // Persist current selections so other components (e.g., chat widget) can consume them
  useEffect(() => {
    localStorage.setItem(STORAGE.HEADER_COLORS, JSON.stringify(headerColors));
    localStorage.setItem(STORAGE.BUBBLE_COLORS, JSON.stringify(bubbleColors));
    localStorage.setItem(STORAGE.HEADER_ANGLE, String(headerAngle));
    localStorage.setItem(STORAGE.BUBBLE_ANGLE, String(bubbleAngle));
    localStorage.setItem(STORAGE.HEADER_GRADIENT, headerGradient);
    localStorage.setItem(STORAGE.BUBBLE_GRADIENT, bubbleGradient);

    // Fire a custom event so live components in the same tab can react instantly
    window.dispatchEvent(
      new CustomEvent(THEME_EVENT, {
        detail: {
          headerGradient,
          bubbleGradient,
          headerColors,
          bubbleColors,
          headerAngle,
          bubbleAngle,
        },
      })
    );
  }, [headerGradient, bubbleGradient]);

  const updateColors = (setter) => (id, val) =>
    setter((prev) => prev.map((c) => (c.id === id ? { ...c, color: val } : c)));

  const addColor = (setter, prefix) => () =>
    setter((prev) => [...prev, { id: `${prefix}-${Date.now()}`, color: "#ffffff" }]);

  const removeColor = (setter) => (id) =>
    setter((prev) => (prev.length <= 2 ? prev : prev.filter((c) => c.id !== id)));

  const saveColor = () => {
    let header_colors = []
    let user_colors = []

    headerColors.forEach((item) => {
      header_colors.push(item.color)
    })
    bubbleColors.forEach((item) => {
      user_colors.push(item.color)
    })

    let header_dict = {
      "color" : header_colors,
      "angle" : headerAngle
    }

    let user_dict = {
      "color" : user_colors,
      "angle" : bubbleAngle
    }

  axios.post(`${endpoint}/api/crawling/add-color` ,
     {"header_gradient" : header_dict , "user_gradient" : user_dict}, 
    {headers : {Authorization : `Bearer ${localStorage.getItem("crawling")}`}})
    .then((response) => {
      // setSnackbar({open : true , message : response?.data?.data?.message || "Saved" , severity : "sucess"})
      setMessage(response?.data?.data?.message || "Saved")
      setOpen(true);
    })
    .catch((error) => {
      const status = error?.response?.status || ""
      if (status === 401 || status === 403) {
        localStorage.removeItem("crawling");
        navigate("/crawling/signin");
        return;
      }
      setMessage(error?.response?.data?.message || error?.response?.data?.detail || "Internal Server Error");
      setOpen(true)
      // setSnackbar({open : true , message : error?.response?.data?.message || error?.response?.data?.detail || "Internal Server Error" , severity : "error"})
    })

}

  return (
    <Box
      sx={{
        maxWidth: 420,
        width: "100%",
        ml: 0,
        mr: "auto",
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
        Chatbot Dynamic Theme
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2.5}>
        Configure gradients for different chatbot areas. Adjust colors and angle to see real-time previews.
      </Typography>

      <ColorList
        label="Header Gradient"
        colors={headerColors}
        angle={headerAngle}
        onAngleChange={setHeaderAngle}
        onColorChange={updateColors(setHeaderColors)}
        onAddColor={addColor(setHeaderColors, "h")}
        onRemoveColor={removeColor(setHeaderColors)}
      />

      <ColorList
        label="Message Bubble Gradient"
        colors={bubbleColors}
        angle={bubbleAngle}
        onAngleChange={setBubbleAngle}
        onColorChange={updateColors(setBubbleColors)}
        onAddColor={addColor(setBubbleColors, "b")}
        onRemoveColor={removeColor(setBubbleColors)}
      />

      <Button variant="outlined" style={{backgroundColor : "green" , color : "white"}} onClick={() => saveColor()}>
        save
      </Button>

      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" variant="filled">
          {message}
        </Alert>
      </Snackbar>
    </Box>
    
  );
};

export default ChatbotDynamicTheme  